import { FunctionComponent, useCallback } from 'react'
import SponsorLogo from '@components/SportTableInline/SponsorLogo'
import { SportTableInlineProps } from '@widgets/SportTableInline/types'
import styled, { css } from 'styled-components'
import Blink from '@components/Blink'
import useTracking, { TrackingFnType } from '@hooks/useTracking'
import useViewportTracking from '@hooks/useViewportTracking'

export type SponsorLinkProps = Required<
  Pick<SportTableInlineProps, 'sponsor'>
> & {
  hasInvertedColors: boolean
}

const StyledSponsorLink = styled(Blink)`
  display: flex;
  text-decoration: none;
  width: fit-content;
`

const SponsorLinkContent = styled.div`
  ${({
    theme: {
      spacing: { spacing8 },
    },
  }) => css`
    display: flex;
    width: fit-content;
    align-items: center;
    margin-top: ${spacing8};
  `}
`

const SponsorLinkText = styled.div<Pick<SponsorLinkProps, 'hasInvertedColors'>>`
  ${({
    theme: {
      color: {
        primary: { primary01, primary02 },
        tertiary: { grey200, grey800 },
      },
      spacing: { spacing8 },
      typography: {
        catchword: {
          small1: { bundledCSS: small1CatchwordCSS },
        },
      },
      utils: { ellipsis },
    },
    hasInvertedColors,
  }) => css`
    color: ${hasInvertedColors ? primary02 : primary01};
    ${small1CatchwordCSS};
    text-transform: uppercase;
    margin-right: ${spacing8};
    text-align: right;

    &:hover {
      color: ${hasInvertedColors ? grey200 : grey800};
    }

    ${ellipsis('100%', 1)};
  `}
`

const SponsorLink: FunctionComponent<SponsorLinkProps> = ({
  sponsor,
  hasInvertedColors,
}) => {
  const onClickTrackingFn = useCallback<TrackingFnType>(
    () => ({
      element: 'table_sponsoring',
      event: 'element_click',
      link_url: sponsor.link?.href,
    }),
    [sponsor.link?.href]
  )

  const onImpressionTrackingFn = useCallback<TrackingFnType>(
    () => ({
      element: 'table_sponsoring',
      event: 'element_impression',
      link_url: sponsor.link?.href,
    }),
    [sponsor.link?.href]
  )

  const onClick = useTracking(onClickTrackingFn)
  const onImpression = useTracking(onImpressionTrackingFn)

  const viewportRef = useViewportTracking({
    onImpression,
    track: true,
  })

  return sponsor.link?.href ? (
    <StyledSponsorLink
      {...sponsor.link}
      ariaLabel={sponsor.text}
      onClick={onClick}
      ref={viewportRef}>
      <SponsorLinkContent>
        {sponsor.text && (
          <SponsorLinkText hasInvertedColors={hasInvertedColors}>
            {sponsor.text}
          </SponsorLinkText>
        )}
        <SponsorLogo image={sponsor.image} />
      </SponsorLinkContent>
    </StyledSponsorLink>
  ) : (
    <SponsorLinkContent ref={viewportRef}>
      {sponsor.text && (
        <SponsorLinkText hasInvertedColors={hasInvertedColors}>
          {sponsor.text}
        </SponsorLinkText>
      )}
      <SponsorLogo image={sponsor.image} />
    </SponsorLinkContent>
  )
}

export default SponsorLink
